import { alpha, createTheme, darken, getContrastRatio, rgbToHex, Theme } from '@mui/material/styles';
import { Branding } from './services/branding.service';

/**
 * The look, taken from the Hack in a Box icon: a cardboard box with near-black outlines and terminal-green pixel type.
 * Kraft-paper page with a faint grain, warm near-black ink, the icon's green for actions and anything live, cardboard
 * copper as the accent. Hero areas are kraft graph paper with a green glow; the live moments are terminal-black panels
 * that glow green. Fonts are the device's own (nothing downloaded): its UI font for text and big bold headlines, its
 * monospace for labels, tags and figures. A customer's branding replaces the colours and fonts on their events.
 */
const GRID = 'rgba(26, 21, 17, 0.07)';
export const LOOK = {
    ink: '#1a1511',
    inkSoft: '#5a5047',
    cream: '#f6f1e7',
    paper: '#fffcf6',
    line: '#e4d9c6',
    muted: '#eee6d6',
    // cardboard, light to dark
    kraft: '#ebcfa6',
    kraftInk: '#6b3915',
    copper: '#c8763a',
    // the icon's green, and the terminal it glows on
    green: '#2bd14a',
    terminal: '#16130f',
    // hero areas: kraft graph paper with a green glow, fading into the page (full-width heroes)
    hero: `linear-gradient(${GRID} 1px, transparent 1px) 0 0 / 32px 32px, linear-gradient(90deg, ${GRID} 1px, transparent 1px) 0 0 / 32px 32px, radial-gradient(70% 80% at 78% 18%, rgba(43, 209, 74, 0.16), transparent 62%), linear-gradient(180deg, #f1dfc2 0%, #f6f1e7 100%)`,
    // the same for a framed panel: it ends on kraft, so the panel's edge still shows against the page
    heroPanel: `linear-gradient(${GRID} 1px, transparent 1px) 0 0 / 28px 28px, linear-gradient(90deg, ${GRID} 1px, transparent 1px) 0 0 / 28px 28px, radial-gradient(70% 90% at 82% 15%, rgba(43, 209, 74, 0.18), transparent 60%), linear-gradient(180deg, #f3e4cb 0%, #ecd6b3 100%)`,
    // a fine film grain, laid over the page
    grain: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.1 0 0 0 0 0.08 0 0 0 0 0.07 0 0 0 0.07 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
};

export const DEFAULT_PRIMARY = LOOK.green;
export const DEFAULT_ACCENT = LOOK.copper;

// default fonts; a customer's brand font replaces both
// web-safe: what each device already has, so nothing is downloaded (Segoe UI on Windows, San Francisco on Apple,
// Roboto on Android; Consolas, SF Mono or Menlo for monospace)
export const SYSTEM_FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const SYSTEM_MONO = 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", "Courier New", monospace';

declare module '@mui/material/styles' {
    interface Theme {
        brand: BrandTokens;
    }
    interface ThemeOptions {
        brand?: BrandTokens;
    }
}

export interface BrandTokens {
    // colors as chosen by the customer, used for large fills like the hero banner
    rawPrimary: string;
    rawAccent: string;
    gradient: string;
    logoUrl?: string;
    bannerUrl?: string;
    displayName?: string;
    isCustom: boolean;
    // the customer chose a primary colour (else hero areas use the kraft graph paper)
    hasColor: boolean;
    // labels, tags and small figures
    mono: string;
}

// a brand font first, the device's own behind it
const fontStack = (font: string) => `"${font}", ${SYSTEM_FONT}`;

/**
 * Darkens a color until it is readable as text/UI on white (WCAG 3:1 for UI components),
 * so a customer picking a light brand color like yellow still gets legible buttons and links.
 */
export const ensureReadableOnWhite = (color: string, minRatio = 3) => {
    let result = color;
    for (let i = 0; i < 20 && getContrastRatio(result, '#ffffff') < minRatio; i++) {
        result = rgbToHex(darken(result, 0.08));
    }
    return result;
}

export const isLowContrast = (color: string | undefined) => {
    return !!color && getContrastRatio(color, '#ffffff') < 3;
}

export const createAppTheme = (branding: Branding = {}): Theme => {
    const rawPrimary = branding.primaryColor || DEFAULT_PRIMARY;
    const rawAccent = branding.accentColor || DEFAULT_ACCENT;
    const primary = ensureReadableOnWhite(rawPrimary);
    const accent = ensureReadableOnWhite(rawAccent);
    // a brand font is one family for everything, labels included
    const bodyFont = branding.fontFamily ? fontStack(branding.fontFamily) : SYSTEM_FONT;
    const headingFont = bodyFont;
    const monoFont = branding.fontFamily ? fontStack(branding.fontFamily) : SYSTEM_MONO;

    const heading = {
        fontFamily: headingFont,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.08
    };

    const theme = createTheme({
        brand: {
            rawPrimary,
            rawAccent,
            gradient: branding.primaryColor
                ? `linear-gradient(135deg, ${darken(rawPrimary, 0.35)} 0%, ${rawPrimary} 55%, ${rawAccent} 130%)`
                : LOOK.hero,
            logoUrl: branding.logoUrl,
            bannerUrl: branding.bannerUrl,
            displayName: branding.displayName,
            isCustom: Object.values(branding).some(Boolean),
            hasColor: !!branding.primaryColor,
            mono: monoFont
        },
        palette: {
            primary: { main: primary, contrastText: getContrastRatio(primary, '#ffffff') >= 3 ? '#ffffff' : LOOK.ink },
            secondary: { main: accent },
            // MUI's info is a cold blue; here it's the box's brown (the scroll-to-top button, info chips)
            info: { main: '#8a5a2b', contrastText: '#ffffff' },
            background: {
                default: LOOK.cream,
                paper: LOOK.paper
            },
            text: {
                primary: LOOK.ink,
                secondary: LOOK.inkSoft
            },
            divider: LOOK.line
        },
        shape: {
            borderRadius: 12
        },
        typography: {
            fontFamily: bodyFont,
            h1: { ...heading, fontSize: '3rem', letterSpacing: '-0.03em', lineHeight: 1 },
            h2: { ...heading, fontSize: '2.25rem' },
            h3: { ...heading, fontSize: '1.625rem' },
            h4: { ...heading, fontSize: '1.3rem' },
            h5: { ...heading, fontSize: '1.125rem' },
            h6: { ...heading, fontSize: '1rem', fontWeight: 600 },
            subtitle1: { fontWeight: 600 },
            button: {
                textTransform: 'none',
                fontWeight: 600
            },
            // labels read like a terminal
            overline: {
                fontFamily: monoFont,
                fontWeight: 500,
                letterSpacing: '0.08em'
            }
        }
    });

    const ink = theme.palette.text.primary;

    return createTheme(theme, {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ':root': {
                        '--brand-primary': primary,
                        '--brand-accent': accent,
                        '--surface-muted': LOOK.muted,
                        '--border-color': LOOK.line
                    },
                    body: {
                        backgroundColor: LOOK.cream,
                        backgroundImage: LOOK.grain,
                        color: ink
                    },
                    // many pages still use raw heading tags - keep them on the type scale
                    'h1:not([class*="MuiTypography"])': { ...theme.typography.h2, margin: 0 },
                    'h2:not([class*="MuiTypography"])': { ...theme.typography.h3, margin: 0 },
                    'h3:not([class*="MuiTypography"])': { ...theme.typography.h4, margin: 0 },
                    'h4:not([class*="MuiTypography"])': { ...theme.typography.h5, margin: 0 },
                    a: {
                        color: primary,
                        textDecorationColor: alpha(primary, 0.35),
                        textUnderlineOffset: '0.2em',
                        '&:hover': {
                            textDecorationColor: primary
                        }
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        paddingLeft: 18,
                        paddingRight: 18
                    },
                    // a slight lift: lit top edge, soft shadow
                    contained: {
                        boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.18)}, 0 1px 2px ${alpha(LOOK.terminal, 0.2)}`,
                        '&:hover': { boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.18)}, 0 4px 12px ${alpha(LOOK.terminal, 0.2)}` }
                    },
                    outlined: {
                        borderColor: alpha(ink, 0.28),
                        '&:hover': { borderColor: ink, backgroundColor: alpha(ink, 0.04) }
                    },
                    sizeLarge: {
                        padding: '11px 24px'
                    }
                }
            },
            MuiCard: {
                defaultProps: {
                    variant: 'outlined'
                },
                styleOverrides: {
                    root: {
                        borderRadius: 20,
                        borderColor: LOOK.line,
                        backgroundColor: LOOK.paper
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    outlined: {
                        borderColor: LOOK.line
                    },
                    elevation1: {
                        boxShadow: `0 1px 2px ${alpha(LOOK.terminal, 0.06)}`
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 500
                    },
                    // the look's tag: a thin ink outline
                    outlined: {
                        borderColor: alpha(ink, 0.45)
                    }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 14,
                        border: '1px solid transparent'
                    },
                    // notices in the look's own tints (MUI's defaults are a cold blue, green, amber and red)
                    standardInfo: { backgroundColor: '#f4e7d2', borderColor: '#e8d3b2', color: LOOK.ink, '& .MuiAlert-icon': { color: LOOK.kraftInk } },
                    standardSuccess: { backgroundColor: '#e2f4e1', borderColor: '#c4e8c6', color: '#123d1f', '& .MuiAlert-icon': { color: '#15852c' } },
                    standardWarning: { backgroundColor: '#fbe6cf', borderColor: '#f0cfa8', color: LOOK.kraftInk, '& .MuiAlert-icon': { color: LOOK.copper } },
                    standardError: { backgroundColor: '#fbe2dc', borderColor: '#f2c4b9', color: '#6e1a12', '& .MuiAlert-icon': { color: '#b42318' } }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#fffefa',
                        borderRadius: 10
                    },
                    notchedOutline: {
                        borderColor: alpha(ink, 0.2)
                    }
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '0.8rem',
                        backgroundColor: LOOK.terminal
                    }
                }
            }
        }
    });
}

const theme = createAppTheme();

export default theme;
